import { Typography } from "@circle-react-uikit/Typography";
import { useIsAgents2BillingEnabled } from "@circle-react/components/CircleAi/useCircleAiFeatureFlags";
import {
  PRODUCT_UTM_CAMPAIGNS,
  triggerChiliPiperProductRouter,
} from "@circle-react/components/CommunityOnboarding/NewChecklistComponents/CircleAppLinks/chiliPiperUtils";
import { SanitizeHTMLString } from "@circle-react/components/shared/uikit/SanitizeContent";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useCircleV4 } from "@circle-react/hooks/useCircleV4";
import { useSupportAskCircleAiCta } from "@circle-react/pages/UnifiedInbox/HumanSupport/hooks/useSupportAskCircleAiCta";
import { Alert } from "@circleco/compass/components/Alert";
import { useEffect, useRef } from "react";

import { t } from "@/i18n-js/instance";

import { WarningWrapper } from "./WarningWrapper";

export const ExpiredTrial = () => {
  const { currentCommunity, currentCommunityMember, currentUser } =
    usePunditUserContext();
  const { isCircleV4 } = useCircleV4();
  const isAgents2BillingEnabled = useIsAgents2BillingEnabled();
  const { handleAskCircleAi, isAskCircleAiLoading } =
    useSupportAskCircleAiCta();
  const containerRef = useRef<HTMLDivElement>(null);

  const isSalesIcp = currentCommunity?.is_sales_icp ?? false;

  /**
   * Attach click handler directly to the link using vanilla JavaScript.
   * This keeps the translation as a single string (translator-friendly) while
   * allowing us to trigger the Chili Piper modal instead of following the link.
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const link = container.querySelector<HTMLAnchorElement>(
      'a[data-chili-piper="expired-trial"]',
    );
    if (!link) return;

    const handleClick = (e: Event) => {
      e.preventDefault();
      if (currentUser?.email) {
        void triggerChiliPiperProductRouter(
          currentUser.email,
          PRODUCT_UTM_CAMPAIGNS.TRIAL_EXPIRED_BANNER,
        );
      }
    };

    link.addEventListener("click", handleClick);
    return () => {
      link.removeEventListener("click", handleClick);
    };
  }, [currentUser?.email]);

  if (isCircleV4) {
    return (
      <Alert
        variant="warning"
        icon="triangle-exclamation"
        iconColor="warning"
        title={t("subscription.expired_trial_title")}
        description={
          isSalesIcp
            ? t(
                isAgents2BillingEnabled
                  ? "pricing_plans.expired_trial_sales_icp_description_agents_2"
                  : "pricing_plans.expired_trial_sales_icp_description",
              )
            : t(
                isAgents2BillingEnabled
                  ? "pricing_plans.expired_trial_non_icp_description_agents_2"
                  : "pricing_plans.expired_trial_non_icp_description",
              )
        }
        secondaryActionProps={{
          children: isSalesIcp
            ? t("pricing_plans.feature.talk_to_sales")
            : t("pricing_plans.chat_with_us"),
          onClick: isSalesIcp
            ? () => {
                if (currentCommunityMember?.email) {
                  void triggerChiliPiperProductRouter(
                    currentCommunityMember.email,
                    PRODUCT_UTM_CAMPAIGNS.TRIAL_EXPIRED_BANNER,
                  );
                }
              }
            : handleAskCircleAi,
          disabled: !isSalesIcp && isAskCircleAiLoading,
        }}
      />
    );
  }

  return (
    <WarningWrapper>
      <Typography.LabelLg weight="semibold">
        {t("subscription.expired_trial_title")}
      </Typography.LabelLg>
      <Typography.BodyMd color="text-default">
        {t("pricing_plans.to_access_upgrade", {
          community_name: currentCommunity?.name,
        })}
        <div ref={containerRef}>
          <SanitizeHTMLString content={t("pricing_plans.please_reach_out")} />
        </div>
      </Typography.BodyMd>
    </WarningWrapper>
  );
};
